import React, { useCallback, useMemo, type MouseEvent } from 'react';
import Button from '@atlaskit/button';

import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';

import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import {
	useChildIssuesLimit,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { getChildIssuesLimitExceededAnalyticsCount } from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectKey,
	useProjectType,
	useIsSimplifiedProject,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { generateViewInSearchLink } from '../../../common/utils/generate-view-in-search-link/index.tsx';
import type { ChildIssuesPanelType } from '../../../common/types.tsx';
import { VIEW_IN_SEARCH_BUTTON_TEST_ID } from '../constants/index.tsx';
import messages from './messages.tsx';

type Props = {
	hasExceededIssuesLimitInitialLoad: boolean;
	childIssuesPanelType: ChildIssuesPanelType;
};

export const ViewInSearchButton = ({
	hasExceededIssuesLimitInitialLoad,
	childIssuesPanelType,
}: Props) => {
	const { formatMessage } = useIntl();
	const issueKey = useIssueKey();
	const { push } = useRouterActions();

	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const isSimplifiedProject = useIsSimplifiedProject(projectKey);

	const childIssuesLimit = useChildIssuesLimit();

	const viewInSearchLink = useMemo(() => {
		// These values will never be missing when the FF is enabled, this check is to satisfy flow
		if (
			projectKey === undefined ||
			projectType === undefined ||
			isSimplifiedProject === undefined
		) {
			logger.safeErrorWithoutCustomerData(
				'issue.child-issues-panel.child-issues-limit-panel',
				'Missing project key, type, or isSimplified when generating search link',
			);
			return '';
		}

		return generateViewInSearchLink(
			projectKey,
			projectType,
			isSimplifiedProject,
			issueKey,
			childIssuesPanelType,
			hasExceededIssuesLimitInitialLoad,
		);
	}, [
		childIssuesPanelType,
		hasExceededIssuesLimitInitialLoad,
		isSimplifiedProject,
		issueKey,
		projectKey,
		projectType,
	]);

	const onViewInSearchClicked = useCallback(
		(event: MouseEvent, analyticsEvent: AnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'viewInSearch', {
				issueCount: getChildIssuesLimitExceededAnalyticsCount(childIssuesLimit),
			});

			// Ignore cmd or ctrl clicks - allow user to open in new tab
			if (!event.metaKey && !event.ctrlKey) {
				event.preventDefault();
				push(viewInSearchLink);
			}
		},
		[childIssuesLimit, push, viewInSearchLink],
	);

	return (
		<Button
			appearance="default"
			href={viewInSearchLink}
			onClick={onViewInSearchClicked}
			testId={VIEW_IN_SEARCH_BUTTON_TEST_ID}
			interactionName="auto-generated.jira-issue-view-common-views.child-issues-panel.view.child-issues-limit-panel.button.view-in-search"
		>
			{formatMessage(messages.viewInSearchButton)}
		</Button>
	);
};
